import React from 'react';
import { Image as ImageIcon, Plus } from 'lucide-react';
import type { OfferAttachment, OfferDocumentContentBlock } from '../../../types';
import { useOfferDocumentIssued } from './offerDocumentIssuedContext';
import { OfferInlineEdit } from './OfferInlineEdit';
import { FirstPageAttachmentImageGrid } from './FirstPageAttachmentImageGrid';
import { OfferPaymentTermsModal } from './OfferPaymentTermsControls';
import type { OfferDocumentPaymentTerms } from '../utils/offerDocumentMetaPatch';
import type { PaymentTermsContext } from '../../../utils/paymentTerms';

type Props = {
  leadRouteId: string;
  blocks: OfferDocumentContentBlock[];
  minTextBlocks?: number;
  variant?: 'default' | 'muted';
  remainingImageSlots: number;
  /** `end` = nach dem letzten Block; `beforeFirstText` = über dem ersten Absatz (Zahlungsziel). */
  insertPlacement?: 'end' | 'beforeFirstText';
  onCommitText: (id: string, content: string) => void | Promise<void>;
  onRemoveBlock: (id: string) => void | Promise<void>;
  onAddText: (index: number) => void | Promise<void>;
  onAddImages: (index: number) => void;
  onRemoveImage: (attachment: OfferAttachment) => void | Promise<void>;
  onReplaceImage: (attachment: OfferAttachment) => void;
  paymentTermsEdit?: {
    blockId: string;
    terms: OfferDocumentPaymentTerms;
    wording: string;
    context: PaymentTermsContext;
    onSave: (next: { terms: OfferDocumentPaymentTerms; wording: string }) => void | Promise<void>;
  } | null;
};

const actionBtnClass =
  'inline-flex items-center gap-1 text-[9pt] text-sky-500 hover:text-sky-600 font-medium disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:text-slate-400';

function InsertActions({
  index,
  remainingImageSlots,
  onAddText,
  onAddImages,
}: {
  index: number;
  remainingImageSlots: number;
  onAddText: (index: number) => void | Promise<void>;
  onAddImages: (index: number) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 mb-1">
      <button type="button" onClick={() => void onAddText(index)} className={actionBtnClass}>
        <Plus className="w-3.5 h-3.5 shrink-0" />
        Absatz hinzufügen
      </button>
      <button
        type="button"
        onClick={() => onAddImages(index)}
        disabled={remainingImageSlots <= 0}
        className={actionBtnClass}
        title={
          remainingImageSlots <= 0
            ? 'Maximal 12 Bilder im Dokument. Bitte zuerst Bilder entfernen.'
            : undefined
        }
      >
        <ImageIcon className="w-3.5 h-3.5 shrink-0" />
        Bilder hinzufügen
      </button>
      {remainingImageSlots <= 0 ? (
        <span className="text-[8.5pt] text-app-text-subtle">Maximal 12 Bilder im Dokument</span>
      ) : null}
    </div>
  );
}

export function OfferDocumentContentList({
  leadRouteId,
  blocks,
  minTextBlocks = 0,
  variant = 'default',
  remainingImageSlots,
  insertPlacement = 'end',
  onCommitText,
  onRemoveBlock,
  onAddText,
  onAddImages,
  onRemoveImage,
  onReplaceImage,
  paymentTermsEdit,
}: Props) {
  const issued = useOfferDocumentIssued();
  const [paymentTermsOpen, setPaymentTermsOpen] = React.useState(false);
  const paymentTermsAnchorRef = React.useRef<HTMLButtonElement>(null);
  const paymentTermsBlockId = paymentTermsEdit?.blockId ?? null;
  const textClass = variant === 'muted' ? 'text-neutral-800' : 'text-neutral-900';
  const textCount = blocks.filter((block) => block.type === 'text').length;
  const insertBeforeIdRef = React.useRef<string | null>(null);
  if (insertPlacement === 'beforeFirstText') {
    const stillPresent =
      insertBeforeIdRef.current != null && blocks.some((block) => block.id === insertBeforeIdRef.current);
    if (!stillPresent) {
      insertBeforeIdRef.current = blocks.find((block) => block.type === 'text')?.id ?? null;
    }
  } else {
    insertBeforeIdRef.current = null;
  }
  const primaryInsertIndex =
    insertPlacement === 'beforeFirstText'
      ? Math.max(
          0,
          insertBeforeIdRef.current
            ? blocks.findIndex((block) => block.id === insertBeforeIdRef.current)
            : 0,
        )
      : blocks.length;
  const showStartInsert = !issued && blocks.length > 0 && primaryInsertIndex > 0;
  const renderInsert = (index: number) =>
    issued ? null : (
      <InsertActions
        index={index}
        remainingImageSlots={remainingImageSlots}
        onAddText={onAddText}
        onAddImages={onAddImages}
      />
    );

  return (
    <div className="space-y-3">
      {showStartInsert ? renderInsert(0) : null}
      {blocks.map((block, i) => {
        const showInsertBefore = !issued && i === primaryInsertIndex;
        if (block.type === 'imageGrid') {
          return (
            <React.Fragment key={block.id}>
              {showInsertBefore ? renderInsert(i) : null}
              <FirstPageAttachmentImageGrid
                leadRouteId={leadRouteId}
                attachments={block.images}
                readOnly={issued}
                onRemove={onRemoveImage}
                onReplace={onReplaceImage}
              />
            </React.Fragment>
          );
        }
        const isTitle = block.type === 'title';
        return (
          <React.Fragment key={block.id}>
            {showInsertBefore ? renderInsert(i) : null}
            <div className="group relative pl-0">
              {!issued && !isTitle && paymentTermsEdit && block.id === paymentTermsBlockId ? (
                <>
                <button
                  ref={paymentTermsAnchorRef}
                  type="button"
                  onClick={() => setPaymentTermsOpen(true)}
                  className="w-full rounded-md text-left transition-colors hover:bg-app-hover/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/60 focus-visible:ring-offset-1"
                  aria-label="Zahlungsziel bearbeiten"
                >
                  <span
                    className={`block whitespace-pre-wrap break-words text-[9.5pt] leading-[1.45] ${
                      block.content.trim() ? textClass : 'italic text-neutral-400'
                    }`}
                  >
                    {block.content.trim() || 'Zahlungsziel – klicken zum Bearbeiten'}
                  </span>
                </button>
                <OfferPaymentTermsModal
                  open={paymentTermsOpen}
                  onClose={() => setPaymentTermsOpen(false)}
                  anchorRef={paymentTermsAnchorRef}
                  terms={paymentTermsEdit.terms}
                  wording={paymentTermsEdit.wording}
                  context={paymentTermsEdit.context}
                  onSave={paymentTermsEdit.onSave}
                />
                </>
              ) : (
              <OfferInlineEdit
                value={block.content}
                onSave={(next) => onCommitText(block.id, next)}
                multiline={!isTitle}
                viewTextClassName={
                  isTitle
                    ? 'text-[9.5pt] font-bold text-neutral-900'
                    : `text-[9.5pt] leading-[1.45] ${textClass}`
                }
                emptyHint={
                  isTitle
                    ? 'Überschrift – klicken zum Bearbeiten'
                    : 'Leerer Absatz – klicken zum Bearbeiten'
                }
                aria-label={isTitle ? 'Überschrift' : `Absatz ${i + 1}`}
              />
              )}
              {!issued && (isTitle || textCount > minTextBlocks) ? (
                <button
                  type="button"
                  className="mt-1 text-[9pt] text-red-600 hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => void onRemoveBlock(block.id)}
                  aria-label={isTitle ? 'Überschrift entfernen' : 'Absatz entfernen'}
                >
                  {isTitle ? 'Überschrift entfernen' : 'Absatz entfernen'}
                </button>
              ) : null}
            </div>
          </React.Fragment>
        );
      })}
      {issued || (insertPlacement === 'beforeFirstText' && primaryInsertIndex < blocks.length)
        ? null
        : renderInsert(primaryInsertIndex)}
    </div>
  );
}
